import {
  collectAssetFields,
  findAssetTypeDescriptor,
  resolveAssetDisplayName
} from '@sollapay/domain/asset-types';

import {buildAssetDisplayNameCopy} from '@/features/buyers/utils';

import type {AssetFieldKey} from '@sollapay/domain/asset-types';
import type {AssetSummaryDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';

const FIELDS = 'view.buyers.detail.assetFields';

export interface AssetFact {
  key: AssetFieldKey;
  label: string;
  value: string;
}

export interface AssetHeaderModel {
  title: string;
  /** Null when the title is already the type's name — no element repeats its neighbour. */
  typeBadge: string | null;
  facts: AssetFact[];
  showEmptyState: boolean;
}

export function getAssetHeaderModel(asset: AssetSummaryDTO, t: TFunction): AssetHeaderModel {
  const {typeName, title, consumed} = resolveAssetDisplayName(asset, buildAssetDisplayNameCopy(t));

  const facts = collectAssetFields(asset.assetType, asset, {exclude: consumed}).map(entry => ({
    key: entry.key,
    // Short form where one exists — a header has no room for the create form's "מספר דירה".
    label: t([`${FIELDS}.short.${entry.labelToken}`, `${FIELDS}.${entry.labelToken}`]),
    value: entry.key === 'area' ? formatArea(entry.value, t) : entry.value
  }));

  return {
    title: title || typeName || asset.displayCode,
    typeBadge: title ? typeName || null : null,
    facts,
    showEmptyState: facts.length === 0 && hasUnconsumedFields(asset, consumed)
  };
}

/** A DEVELOPER_UNIT's title consumes both of its fields, so nothing was ever left unfilled. */
function hasUnconsumedFields(asset: AssetSummaryDTO, consumed: AssetFieldKey[]): boolean {
  const descriptor = findAssetTypeDescriptor(asset.assetType);
  return !!descriptor?.fields.some(field => !consumed.includes(field.key));
}

/** The `numeric(12,2)` column hands back `240.00`; a header shows `240`. */
function formatArea(value: string, t: TFunction): string {
  const trimmed = value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
  return `${trimmed} ${t(`${FIELDS}.areaUnit`)}`;
}
